import { useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import { RootState } from "src/redux/store";
import { UserAgentDetailsResponse } from "../types/user-agents.type";
import { useState, useEffect } from "react";

interface IUseSiteGBP1to1Mapping {
  siteId: string;
  /** Optional pre-fetched agent details (used during onboarding when react-query cache may be empty) */
  userAgentDetails?: UserAgentDetailsResponse;
  agent?: 'local_seo' | 'review-automation'
}

export interface ISiteAITask {
  task_id: string;
  title: string;
  status: string;
  _id: string;
  created_at: number;
  updated_at: number;
}

export interface ISiteOrbitAI {
  keyword_assist: {
    enabled: boolean;
    competitors: string[];
  };
  content_strategy: {
    enabled: boolean;
    competitors: string[];
  };
  blog_generation: {
    enabled: boolean;
    topics: string[];
    keywords: string[];
  };
  enabled: boolean;
  setup_completed: boolean;
  updated_at: number;
}

export interface ISiteDetails {
  _id: string;
  title: string;
  squarespace_unique_id: string;
  url: string;
  user_id: string;
  created_by: string;
  total_optimized: number;
  total_scanned: number;
  sites_audit: boolean;
  active_status: string;
  total_sub_pages: number;
  total_scan_sub_pages: number;
  squarespace_disconnected: boolean;
  disable_disconnect_popup: boolean;
  update_scan_count: boolean;
  selected_image_size: number;
  email_list: string[];
  celebration_flag: boolean;
  is_audit: boolean;
  selected_language_url: string | null;
  ai_visibility: boolean;
  isDelete: boolean;
  selected_model: string[];
  ai_visibility_used: boolean;
  monitoring_in_progress: boolean;
  orbit_ai: ISiteOrbitAI;
  created_at: number;
  monitoring_time: number;
  ai_tasks: ISiteAITask[];
  __v: number;
  monitoring: string;
  isGsc: boolean;
}

export interface ILocalSEOConfigs {
  agentEnabled: boolean;
  autoApplyEnabled: boolean;
}

export interface IUseSiteGBP1to1MappingResponse {
  isConfigurationError: boolean;
  connectedGBPAccount: IGBPLocation | undefined;
  connectedSite: ISiteDetails | null;
  GBPLocations: IGBPLocation[];
  isGBPConnected: boolean;
  siteList: ISiteDetails[];
  isLocalSEOEnabled: ILocalSEOConfigs;
  isReviewAutomationEnabled: boolean;
  /** Programmatically select a GBP location (updates the connected account locally) */
  selectGBP: (location: IGBPLocation) => void;
  /** URL of the site that the agent is configured to (when configured to another site) */
  configuredToSiteURL: string;
}

// GBP location
export interface IGBPKeyword {
  id: number;
  name: string;
}

export interface IGBPLocation {
  name: string;
  title: string;
  address: string;
  createdTime: number;
  websiteUri: string;
  selected: boolean;
  usedAddonCredit: boolean;
  reportId: number;
  locationId: number;
  keywords: IGBPKeyword[];
  progressing: boolean;
  accountId: string;
  isConnectedWithSqsp: boolean;
  orbitLocationId?: string;
}

const useSiteGBP1to1Mapping = (configs: IUseSiteGBP1to1Mapping): IUseSiteGBP1to1MappingResponse => {

  const currentSite = useSelector((state: RootState) => state?.siteAudit?.siteAuditDetails);
  const siteList: ISiteDetails[] = useSelector((state: RootState) => state?.siteAudit?.auditSiteDetails);
  const GBPLocations: IGBPLocation[] = useSelector((state: RootState) => state.localSeo.reduxFilteredLocationsData) || [];

  const [isConfigurationError, setIsConfigurationError] = useState<boolean>(false);
  const [connectedGBPAccount, setConnectedGBPAccount] = useState<IGBPLocation | undefined>(undefined);
  const [connectedSite, setConnectedSite] = useState<ISiteDetails | null>(null);
  const [isLocalSEOEnabled, setIsLocalSEOEnabled] = useState<ILocalSEOConfigs>({ agentEnabled: false, autoApplyEnabled: false })
  const [isReviewAutomationEnabled, setIsReviewAutomationEnabled] = useState<boolean>(false)


  // Use provided userAgentDetails or fall back to react-query cache
  const queryClient = useQueryClient();
  const siteId = configs?.siteId;
  const cachedAgentDetails = queryClient.getQueryData<UserAgentDetailsResponse>([
    'getUserAgentDetails',
    siteId,
  ]);
  const userAgentDetails = configs.userAgentDetails || cachedAgentDetails;
  const [configuredToSiteURL, setConfiguredToSiteURL] = useState<string>('');

  // Resolve site URL from siteList by siteId
  const resolveSiteURL = (targetSiteId?: string): string => {
    if (!targetSiteId) return '';
    const matched = siteList?.find(
      (s) => String(s._id) === String(targetSiteId)
    );
    return matched?.url || '';
  };

  /** Programmatically select a GBP location */
  const selectGBP = (location: IGBPLocation) => {
    if (!location) return;
    setConnectedGBPAccount(location);
    setIsConfigurationError(false);
  };

  // find connected GBP with site
  useEffect(() => {
    if (!userAgentDetails?.response) return;

    const localSeoSiteId = userAgentDetails.response.local_seo?.site_id;
    const reviewAutomationSiteId = userAgentDetails.response.review_automation?.site_id;

    const localSeoMatchesSite = Boolean(localSeoSiteId && configs.siteId && String(localSeoSiteId) === String(configs.siteId));
    const reviewAutomationMatchesSite = Boolean(reviewAutomationSiteId && configs.siteId && String(reviewAutomationSiteId) === String(configs.siteId));
    const isGBPnSiteConnected = localSeoMatchesSite || reviewAutomationMatchesSite;

    const localSeoOtherSite = Boolean(localSeoSiteId && configs.siteId && String(localSeoSiteId) !== String(configs.siteId));
    const reviewAutomationOtherSite = Boolean(reviewAutomationSiteId && configs.siteId && String(reviewAutomationSiteId) !== String(configs.siteId));

    // Resolve the "other site" URL for display
    const otherSiteId = localSeoOtherSite ? localSeoSiteId : (reviewAutomationOtherSite ? reviewAutomationSiteId : undefined);
    setConfiguredToSiteURL(resolveSiteURL(otherSiteId));

    if (isGBPnSiteConnected) {
      const effectiveGBPAccountId =
        (localSeoMatchesSite ? userAgentDetails.response.local_seo?.account_id : undefined) ||
        (reviewAutomationMatchesSite ? userAgentDetails.response.review_automation?.account_id : undefined);
      const effectiveGBPLocationId =
        (localSeoMatchesSite ? userAgentDetails.response.local_seo?.location_id : undefined) ||
        (reviewAutomationMatchesSite ? userAgentDetails.response.review_automation?.location_id : undefined);

      const localSeoApiData = userAgentDetails?.response?.local_seo;

      // Guard against empty location/account IDs to prevent false-positive matches
      const mappedGBP = (effectiveGBPAccountId && effectiveGBPLocationId)
        ? GBPLocations?.find((location) => {
            const locAccId = String(location.accountId || '');
            const targetAccId = String(effectiveGBPAccountId || '');
            const accountMatch = locAccId === targetAccId;

            const locName = String(location.name || '');
            const targetLocId = String(effectiveGBPLocationId || '');
            // Normalize: location.name may be "locations/123" while targetLocId is "123"
            const locationMatch = locName.includes('/')
              ? locName.split('/')[1] === targetLocId || locName === targetLocId
              : locName === targetLocId;

            return accountMatch && locationMatch;
          })
        : undefined;

      setIsLocalSEOEnabled(() => {
        const isEnabled = localSeoApiData?.enabled || false;
        const isAutoApply = localSeoApiData?.auto_apply || false;

        return {
          agentEnabled: isEnabled,
          autoApplyEnabled: isAutoApply
        }

      })

      setIsReviewAutomationEnabled(() => userAgentDetails?.response?.review_automation?.enabled || false)

      if (mappedGBP) {
        setConnectedGBPAccount(mappedGBP)
        setIsConfigurationError(false)

        setConnectedSite(() => {
          const matchingSiteId =
            (localSeoMatchesSite ? userAgentDetails.response.local_seo?.site_id : undefined) ||
            (reviewAutomationMatchesSite ? userAgentDetails.response.review_automation?.site_id : undefined);
          return siteList.find((site) => site._id === matchingSiteId) || null
        })

      } else {
        setIsConfigurationError(true)
        setConnectedGBPAccount(undefined)
        setConnectedSite(null)
      }
    } else {
      setIsConfigurationError(false)
      setConnectedGBPAccount(undefined)
      setConnectedSite(null)
      setIsLocalSEOEnabled(() => {
        return {
          agentEnabled: false,
          autoApplyEnabled: false
        }
      })
    }
  }, [userAgentDetails, configs.siteId, GBPLocations, siteList]);

  const UpdatedGBPLocations = GBPLocations?.map((location) => ({
    ...location,
    orbitLocationId: location.name.split('/')[1]
  }))

  return {
    isConfigurationError,
    connectedGBPAccount,
    connectedSite,
    GBPLocations: UpdatedGBPLocations,
    isGBPConnected: UpdatedGBPLocations?.length > 0,
    siteList,
    isLocalSEOEnabled,
    isReviewAutomationEnabled,
    selectGBP,
    configuredToSiteURL
  }

}

export default useSiteGBP1to1Mapping;